import { useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from '@nobia/zeus-components/lib/i18n/useTranslation'
import settings from '@nobia/zeus-components/lib/settings'
import { toast } from 'react-toastify'

import { useAccount } from '../contexts/AccountProvider'
import { useRoutes } from '../routes'
import { buildCustomerMessagesPageSearch } from '../utils/messagesPageSearchParams'
import { canonicalRoutePath } from '../utils/canonicalRoutePath'
import { Connection } from './signalr'
import {
  getIncomingCustomerId,
  getIncomingStoreId,
  isIncomingCustomerMessage,
} from './chatMessageUtils'
import { getActiveChatThreadCustomerId } from './activeChatThread'

const isOnMessagesPage = (pathname, messagesPath) => {
  const normalizedPath = canonicalRoutePath(pathname)
  const normalizedMessagesPath = canonicalRoutePath(messagesPath)
  return (
    normalizedPath === normalizedMessagesPath ||
    normalizedPath.startsWith(`${normalizedMessagesPath}/`)
  )
}

export function useChatMessageNotifications(enabled) {
  const { t } = useTranslation('pages.customers.details.messages')
  const history = useHistory()
  const location = useLocation()
  const { store } = useAccount()
  const { customerChat, customerMessages } = useRoutes()

  const tRef = useRef(t)
  const historyRef = useRef(history)
  const locationRef = useRef(location)
  const storeRef = useRef(store)
  const customerChatRef = useRef(customerChat)
  const customerMessagesPathRef = useRef(customerMessages.path)

  tRef.current = t
  historyRef.current = history
  locationRef.current = location
  storeRef.current = store
  customerChatRef.current = customerChat
  customerMessagesPathRef.current = customerMessages.path

  useEffect(() => {
    if (!enabled) return undefined

    const openIncomingMessage = (customerId, storeIdFromPayload) => {
      if (settings.brand === 'magnet') {
        const storeId = storeIdFromPayload ?? storeRef.current?.id ?? null
        historyRef.current.push({
          pathname: canonicalRoutePath(customerMessagesPathRef.current),
          search: buildCustomerMessagesPageSearch({ customerId, storeId }),
        })
        return
      }

      const path = customerChatRef.current.calculatePath({ customer: customerId })
      const url = `${settings.basename}${path}`
      window.open(url, '_blank')
    }

    const handleChatMessage = (data) => {
      if (!isIncomingCustomerMessage(data)) return

      const incomingCustomerId = getIncomingCustomerId(data)
      if (!incomingCustomerId) return

      const onMessagesPage = isOnMessagesPage(
        locationRef.current.pathname,
        customerMessagesPathRef.current
      )
      if (
        onMessagesPage &&
        getActiveChatThreadCustomerId() === incomingCustomerId
      ) {
        return
      }

      const incomingStoreId = getIncomingStoreId(data)

      toast.info(tRef.current('new-message-received'), {
        onClick: () =>
          openIncomingMessage(incomingCustomerId, incomingStoreId),
      })
    }

    const unsubscribeMessage = Connection.subscribe(handleChatMessage)

    return () => {
      unsubscribeMessage()
    }
  }, [enabled])
}
